/* ──────────────────────────────────────────────────────────────
   Setup/Locations/index.tsx · Locations (live — sk-catalog-api /locations)
   ────────────────────────────────────────────────────────────── */

import React, { useState, useEffect, useCallback } from 'react';
import { useReloadOnFocus } from '../../navigation/useReloadOnFocus';
import {
  View,
  Text,
  Pressable,
  ScrollView,
  ActivityIndicator,
  ViewStyle,
  TextStyle,
} from 'react-native';

import { useTheme } from '../../context/themeContext';
import {
  Icon,
  Button,
  Chip,
  Input,
  Modal,
  Empty,
  ConfirmDialog,
  useToast,
} from '../../components/ui';
import { getLocations, createLocation, updateLocation, deleteLocation, getPlatformLocations } from '../../services/catalog/location';
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import type { Location, PlatformLocation } from '../../schemas/catalog/location';
import { useApp } from '../../context/appContext';

export default function LocationsView() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;
  const toast = useToast();
  // Notify the topbar location switcher to refetch after any change here.
  const { refreshLocations } = useApp();

  const [locs, setLocs] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Branches come from platform, the owner. Catalog mirrors one by its
  // `loc_<ULID>`; it cannot invent a branch, so there is no free-text name.
  const [branches, setBranches] = useState<PlatformLocation[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [picked, setPicked] = useState<PlatformLocation | null>(null);
  const [delTarget, setDelTarget] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLocations();
      setLocs(res.items ?? []);
    } catch (err) {
      handleApiError(err, 'Could not load locations');
    } finally {
      setLoading(false);
    }
  }, []);

  useReloadOnFocus(load);

  // Load platform's branches when the Add dialog opens, and hide the ones
  // already mirrored so the same branch can't be added twice.
  const openAdd = async () => {
    setPicked(null);
    setAddOpen(true);
    setBranchesLoading(true);
    try {
      setBranches(await getPlatformLocations());
    } catch (err) {
      handleApiError(err, 'Could not load branches from platform');
    } finally {
      setBranchesLoading(false);
    }
  };

  const mirrored = new Set(locs.map((l) => l.id));
  const addable = branches.filter((b) => !mirrored.has(b.id));

  const onAdd = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const res = await createLocation({ id: picked.id, name: picked.name });
      showSuccess(res, 'Location added', `“${picked.name}” created.`);
      setPicked(null);
      setAddOpen(false);
      await load();
      refreshLocations();
    } catch (err) {
      handleApiError(err, 'Could not add location');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await deleteLocation(delTarget.id);
      showSuccess({}, 'Location removed', `“${delTarget.name}” deleted.`);
      setLocs(prev => prev.filter(x => x.id !== delTarget.id));
      setDelTarget(null);
      refreshLocations();
    } catch (err) {
      handleApiError(err, 'Could not delete location');
    } finally {
      setDeleting(false);
    }
  };

  const [renameTarget, setRenameTarget] = useState<Location | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const commitRename = async () => {
    if (!renameTarget || !renameValue.trim()) return;
    setBusy(true);
    try {
      await updateLocation(renameTarget.id, { name: renameValue.trim() });
      showSuccess({}, 'Location renamed', 'Updated.');
      setRenameTarget(null);
      await load();
      refreshLocations();
    } catch (err) {
      handleApiError(err, 'Could not rename location');
    } finally {
      setBusy(false);
    }
  };

  const thStyle: TextStyle = {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.medium,
    color: colors.ink3,
    fontFamily: theme.font.sans,
    textTransform: 'uppercase',
    letterSpacing: 0.04 * fontSize.xs,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.line,
    backgroundColor: colors.paper2,
  };

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.paper }} contentContainerStyle={{ padding: 24, paddingBottom: 64, maxWidth: 860 }}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <View style={{ flex: 1, minWidth: 240 }}>
          <Text style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: theme.font.sans, letterSpacing: -0.02 * fontSize['3xl'] }}>
            Locations
          </Text>
          <Text style={{ fontSize: fontSize.md, color: colors.ink3, fontFamily: theme.font.sans, marginTop: 4 }}>
            Branches &amp; channels. Location-scoped fields (like price and stock) can vary per location.
          </Text>
        </View>
        <Button kind="accent" icon="plus" onPress={openAdd}>Add location</Button>
      </View>

      <View style={{ backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, overflow: 'hidden', ...(shadows.sm as ViewStyle) }}>
        {loading ? (
          <View style={{ paddingVertical: 40, alignItems: 'center' }}>
            <ActivityIndicator color={colors.accent} />
          </View>
        ) : locs.length === 0 ? (
          <View style={{ padding: 16 }}>
            <Empty title="No locations yet">Add your first branch or channel.</Empty>
          </View>
        ) : (
          <View>
            <View style={{ flexDirection: 'row' }}>
              <Text style={[thStyle, { flex: 2, minWidth: 140 }]}>Location</Text>
              <Text style={[thStyle, { flex: 1, minWidth: 132 }]}>Status</Text>
            </View>
            {locs.map((l, idx) => {
              const isLast = idx === locs.length - 1;
              return (
                <View key={l.id} style={{ flexDirection: 'row' }}>
                  <View style={[{ flex: 2, minWidth: 140 }, { borderBottomWidth: isLast ? 0 : 1, borderBottomColor: colors.line, paddingHorizontal: 12, paddingVertical: 13 }]}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                      <View style={{ width: 36, height: 36, borderRadius: radii.r2, backgroundColor: colors.paper3, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
                        <Icon name="map-pin" size={16} color={colors.ink4} />
                      </View>
                      <Text style={{ fontWeight: fontWeight.semibold, fontSize: fontSize.md, color: colors.ink, fontFamily: theme.font.sans }}>
                        {l.name}
                      </Text>
                    </View>
                  </View>
                  <View style={[{ flex: 1, minWidth: 132 }, { borderBottomWidth: isLast ? 0 : 1, borderBottomColor: colors.line, paddingHorizontal: 12, paddingVertical: 13, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, rowGap: 6 }]}>
                    <Chip tone="success" dot>Active</Chip>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                      <Button kind="ghost" size="xs" iconOnly icon="edit" aria-label="Rename" onPress={() => { setRenameTarget(l); setRenameValue(l.name); }} />
                      <Button kind="danger" size="xs" iconOnly icon="trash" aria-label="Delete" onPress={() => setDelTarget(l)} />
                    </View>
                  </View>
                </View>
              );
            })}
          </View>
        )}
      </View>

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add location"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setAddOpen(false)}>Cancel</Button>
            <Button kind="accent" disabled={!picked || busy} onPress={onAdd}>
              {busy ? 'Adding…' : 'Add'}
            </Button>
          </View>
        }
      >
        <View style={{ gap: 12 }}>
          <Text style={{ fontSize: fontSize.sm, color: colors.ink2 }}>
            Branches are managed in your workspace settings. Pick the one to make
            available for catalog pricing.
          </Text>
          {branchesLoading ? (
            <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>Loading branches…</Text>
          ) : addable.length === 0 ? (
            <Empty title={branches.length === 0 ? 'No branches yet' : 'Every branch is already added'}>
              {branches.length === 0
                ? 'Create a branch in workspace settings first, then add it here.'
                : 'All of your branches are already available in the catalog.'}
            </Empty>
          ) : (
            <View style={{ gap: 8 }}>
              {addable.map((b) => {
                const on = picked?.id === b.id;
                return (
                  <Pressable
                    key={b.id}
                    onPress={() => setPicked(b)}
                    style={{
                      padding: 12,
                      borderRadius: radii.r2,
                      borderWidth: 1,
                      borderColor: on ? colors.accent : colors.line,
                      backgroundColor: on ? colors.accentSoft : colors.card,
                      gap: 2,
                    }}
                  >
                    <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.medium, color: colors.ink }}>
                      {on ? '✓ ' : ''}{b.name}
                    </Text>
                    <Text style={{ fontSize: fontSize.xs, color: colors.ink3 }}>
                      {[b.code, b.city, b.timezone].filter(Boolean).join(' · ')}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          )}
        </View>
      </Modal>

      <Modal
        open={!!renameTarget}
        onClose={() => setRenameTarget(null)}
        title="Rename location"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setRenameTarget(null)}>Cancel</Button>
            <Button kind="accent" disabled={!renameValue.trim() || busy} onPress={commitRename}>Save</Button>
          </View>
        }
      >
        <Input label="Location name" value={renameValue} onChangeText={setRenameValue} autoFocus />
      </Modal>

      <ConfirmDialog
        visible={!!delTarget}
        title="Delete location?"
        message={delTarget ? `"${delTarget.name}" will be deleted. (Blocked if it has location-specific values.)` : ''}
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDelTarget(null)}
        onClose={() => setDelTarget(null)}
      />
    </ScrollView>
  );
}
